import { Form } from '@ant-design/compatible';
import { Col, Row } from 'antd';
import ErrorHandler from 'common/error/components/error-handler';
import LinkedList from 'common/list/linked-list';
import Spinner from 'common/spinner/components/spinner';
import debounce from 'lodash.debounce';
import CreateOrders from 'order/create/containers/create-orders-container';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Outlet, Route, Routes } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Div, FormItem, Modal, notification, SubTitle } from 'ui';
import moment from 'utils/cops-moment';
import { withRouter } from 'utils/withRouter';
import {
  Buttons,
  StyledNotificationButton,
} from '../../../common/publish-steps/WarningNotification';
import RichTextEditor from '../../../ui/rich-text-editor';
import { smartActionTypes, ticketCategories, ticketStatus } from '../../../utils/app-configs';
import { checkTags, sanitize, sanitizeNote } from '../../../utils/helpers';
import useCollapseExpand from '../../../utils/hooks/useCollapseExpand';
import Prompt from '../../../utils/prompt';
import SmartActionSection from '../../containers/form/smart-action-section-container';
import TicketToolbarContainer from '../../containers/form/ticket-toolbar-container';
import { category } from '../../ticket-config';
import ActionSidepanel from '../smart-action-sidepanel/action-sidepanel';
import Comments from './comments';
import ConclusionCard from './conclusion-card';
import TicketDescription from './description';
import QuestionWarning from './question-warning';
import SnoozeModal from './snooze-modal';

const StyledForm = styled(Form)`
  .ant-select-selection {
    padding: 0;
  }
  .has-error > span > div {
    border-color: #f04134;
  }
  .has-error > span > div:focus {
    border-color: #f46e65;
    outline: 0;
    box-shadow: 0 0 0 2px rgba(240, 65, 52, 0.2);
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-header {
    text-transform: uppercase;
  }
`;

export const Text = styled.div`
  margin-bottom: 15px;
  color: ${p => p.theme.colors.alerts[2]};
  font-weight: ${p => p.theme.fontWeight.bolder};
  text-transform: uppercase;
`;

const LeftCol = styled(Col)`
  padding-right: 24px;
`;

const RightCol = styled(Col)`
  padding-left: 24px;
`;

const StyledMainRow = styled(Row)`
  margin-top: 24px;
`;

const StyledButton = styled(Button)`
  text-transform: uppercase;
  background-color: ${p => p.theme.colors.darkThemeSecondary} !important;
  color: #fff !important;
  margin-left: 10px;
`;

const Required = styled.span`
  ::before {
    color: rgb(255, 77, 79);
    font-size: 16px;
    content: '*';
    padding-right: 5px;
  }
`;

const TicketForm = ({
  priorities,
  ticket,
  tags,
  comments,
  form,
  createTicket,
  getTicket,
  addComment,
  isLoading,
  isSaving,
  success,
  intl,
  context,
  isCommentsLoading,
  fullPage,
  error,
  resetTicketSuccess,
  renderMenu,
  categoryInfo,
  objectId,
  ticketFullId,
  ticketId,
  linkedSourceObject,
  linkedEntities,
  ticketWatchers,
  saveSmartActionSuccess,
  deleteSmartActionSuccess,
  getCommentsByTicketId,
  getTicketActionTypes,
  isNew,
  fetchRestrictedUsers,
  users,
  isLoadingUsers,
  addCommentSuccess,
  clearUsersData,
  isSnoozing,
  snoozeTicketRequest,
  snoozeSuccess,
  snoozeFailure,
  saveTicket,
  changeNavigationContext,
  match,
}) => {
  const {
    ticketStatusId,
    actions,
  } = useSelector(state => state.ticketForm);
  const { getFieldDecorator } = form;
  const [willSave, setWillSave] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [resetComment, setResetComment] = useState(false);
  const [editDescription, setEditDescription] = useState(false);
  const [resetTags, setResetTags] = useState(false);
  const [linkedObjects, setLinkedObjects] = useState([]);
  const [watchers, setWatchers] = useState([]);
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const debouncedSearch = useRef(
    debounce((v, scope) => fetchRestrictedUsers({
      searchString: v,
      entityScope: scope,
      dropdownId: 'watchers',
    }), 300),
  ).current;
  const [linkedArrow, linkedContent] = useCollapseExpand(false);
  const [watchersArrow, watcherContent] = useCollapseExpand(false);
  const [descriptionArrow, descriptionContent] = useCollapseExpand(false);
  const [commentArrow, commentContent] = useCollapseExpand(false);
  const navigate = useNavigate();

  const id = ticketFullId || ticketId;
  const disabled = ticket?.categoryId === category.diagnostics ?
    ticketStatusId === ticketStatus.closed :
    false;

  useEffect(() => {
    if (linkedObjects?.length) {
      const noneSourceEntityIds = linkedObjects
        ?.filter(i => !i.isSourceObject)
        ?.map(x => x.objectId);
      form.setFieldsValue({
        linkedEntityIds: noneSourceEntityIds,
      });
    }
  }, [linkedObjects]);

  useEffect(() => {
    if (snoozeSuccess || snoozeFailure) {
      setShowSnoozeModal(false);
      notification.destroy('snoozeUntilWarning');
    }
  }, [snoozeSuccess, snoozeFailure]);

  useEffect(() => {
    if ((linkedSourceObject && Object.keys(linkedSourceObject).length) || linkedEntities.length) {
      setLinkedObjects([
        {
          ...linkedSourceObject,
          isSourceObject: true,
        },
        ...linkedEntities,
      ]);
    }
  }, [linkedSourceObject, linkedEntities]);

  useEffect(() => {
    form.setFieldsValue({
      watchers: watchers?.map(w => w.id),
    });
  }, [watchers]);

  useEffect(() => {
    if (ticketWatchers?.length) {
      setWatchers(ticketWatchers);
    }
  }, [ticketWatchers]);

  /**
   * Set isDisabled state
   */
  useEffect(() => {
    setIsDisabled(
      !!(isSaving || isLoading || isCommentsLoading),
    );
  }, [isSaving, isLoading, isCommentsLoading]);

  /**
  * Fetch the ticket when the form is mounted.
  */
  useEffect(() => {
    if (id && !isNew) {
      getTicket({ id, isNew });
    } else if (objectId) {
      const [, type] = location.pathname.match(/type\/(.*)\//);
      createTicket({
        id: objectId,
        type,
      });
    }
  }, [ticketId, objectId]);

  /**
  * Show success notifications when done
   */
  useEffect(() => {
    if (success) {
      if (!snoozeSuccess) {
        notification.success({
          message: intl.formatMessage({ id: 'ticket.SAVE_SUCCESS' }),
          placement: 'top',
        });
      }
      if (ticketFullId) {
        getCommentsByTicketId(ticketFullId);
      } else if (ticket?.number && ticket?.id) {
        navigate(`/ticket/${ticket?.number}/${ticket?.id}`, { replace: false });
        getTicket({ id: ticket?.id, isNew });
        changeNavigationContext({
          context: ticket?.id,
          shouldUpdate: true,
        });
      }
      form.resetFields();
      setIsFormDirty(false);
      setWillSave(false);
    }

    return () => {
      resetTicketSuccess();
    };
  }, [success]);

  useEffect(() => {
    if (resetComment) {
      setResetComment(false);
    }
  }, [resetComment]);

  useEffect(() => {
    form.setFieldsValue({
      priorityId: priorities?.find(x => x.default)?.id,
    });
  }, [priorities]);

  useEffect(() => {
    form.setFieldsValue({
      conclusionTags: ticket?.conclusionTags || [],
    });
  }, [ticket?.conclusionTags]);

  useEffect(() => {
    if (resetTags) {
      setResetTags(false);
    }
  }, [resetTags]);

  useEffect(() => {
    if (snoozeSuccess) {
      getCommentsByTicketId(id);
    }
  }, [snoozeSuccess]);

  useEffect(() => {
    if (saveSmartActionSuccess || deleteSmartActionSuccess) {
      getCommentsByTicketId(ticketFullId);
      getTicketActionTypes({
        objectType: linkedSourceObject?.type,
        id: ticketFullId,
      });
    }
  }, [saveSmartActionSuccess, deleteSmartActionSuccess]);

  useEffect(() => {
    if (isNew) {
      setIsFormDirty(true);
      setEditDescription(true);
    }
  }, [isNew]);

  useEffect(() => {
    if (addCommentSuccess && !form.isFieldsTouched()) {
      setIsFormDirty(false);
    }
  }, [addCommentSuccess]);

  useEffect(() => {
    if (ticket.priorityId) {
      form.setFieldsValue({
        priorityId: ticket.priorityId,
      });
    }
  }, [ticket]);

  const getWarningDescription = (warningQuestions, onConfirm) =>
    <QuestionWarning
      openQuestions={warningQuestions}
      intl={intl}
      form={form}
      allTags={categoryInfo?.conclusionTags}
      onConfirm={onConfirm}
    />;

  const showQuestionNotification = (warningQuestions, onConfirm) => {
    notification.warning({
      message: intl.formatMessage({ id: 'ticket.QUESTION_WARNING_TITLE' }),
      description: getWarningDescription(warningQuestions, onConfirm),
      key: 'openQuestionsWarning',
      style: {
        width: 676,
        marginLeft: -100,
      },
      className: 'extended-notification',
      duration: 0,
      placement: 'top',
    });
  };

  const handleSave = (values, callback) => {
    setWillSave(true);
    saveTicket({
      data: {
        id: ticket.id,
        categoryId: ticket.categoryId,
        isNew,
        objectId: match.params.objectId,
        ...values,
        message: values.message ? sanitizeNote(values.message) : null,
        targetDate:
          values.targetDate && moment(values.targetDate).format('YYYY-MM-DDTHH:mm:ss'),
      },
      callback,
    });
  };

  const save = (callback) => {
    form.validateFields((err, values) => {
      if (!err) {
        const questions = categoryInfo?.conclusionWizard || [];
        const initialOpenQuestions = questions?.filter(q =>
          !values.conclusionTags?.find(c => c.questionId === q.id),
        );
        if (
          parseInt(values.statusId, 10) === ticketStatus.closed &&
          initialOpenQuestions?.length
        ) {
          if (
            actions?.find(a => a.status === 0 && a.actionType?.id !== smartActionTypes.customerDown)
          ) {
            notification?.error({
              message: intl.formatMessage({ id: 'common.ERROR_OCCURED' }),
              description: <>
                <p>{intl.formatMessage({ id: 'tickets.error.saveError' })}</p>
                <ul>
                  <li>{intl.formatMessage(
                    { id: 'tickets.error.cannotMoveBeyondStatusInProgress' },
                  )}</li>
                </ul>
              </>,
              duration: 0,
              className: 'extended-notification',
            });
          } else if (actions?.find(a => a.actionType?.id === smartActionTypes.customerDown)) {
            notification?.warning({
              key: 'follow-up-warning',
              message: intl.formatMessage({ id: 'tickets.error.followUpWarningTitle' }),
              description: <>
                <p>{intl.formatMessage({ id: 'tickets.error.followUpWarning' })}</p>
                <Buttons>
                  <StyledNotificationButton
                    onClick={() => notification.destroy('follow-up-warning')}
                  >
                    {intl.formatMessage({ id: 'common.CANCEL' })}
                  </StyledNotificationButton>
                  <StyledNotificationButton
                    primary={true}
                    onClick={() => {
                      notification.destroy('follow-up-warning');
                      showQuestionNotification(
                        initialOpenQuestions,
                        newValues => handleSave(newValues, callback),
                      );
                    }}
                  >
                    {intl.formatMessage({ id: 'common.CONFIRM' })}
                  </StyledNotificationButton>
                </Buttons>
              </>,
              duration: 0,
              className: 'extended-notification',
            });
          } else {
            showQuestionNotification(
              initialOpenQuestions,
              newValues => handleSave(newValues, callback),
            );
          }
        } else {
          handleSave(values, callback);
        }
      }
    });
  };

  const handleAddLinkedObject = (linkedObjectId) => {
    const object = tags?.find(t => t.objectId === linkedObjectId);
    if (object) {
      setLinkedObjects((lo) => {
        if (!lo?.find(o => o.objectId === linkedObjectId)) {
          return [
            ...lo,
            object,
          ];
        }
        return lo;
      });
    }
  };

  const handleUpdateLinkedObjects = (items) => {
    setIsFormDirty(true);
    setLinkedObjects(items);
  };

  const handleUpdateWatchers = (items) => {
    setIsFormDirty(true);
    setWatchers(items);
  };

  const shouldRenderConclusions = () => ticket?.categoryId !== ticketCategories.general;

  // Set the linkedObjects and watchers to initial value
  // when the changes on the ticket are reverted.
  const resetToInitial = () => {
    setLinkedObjects([
      {
        ...linkedSourceObject,
        isSourceObject: true,
      },
      ...linkedEntities,
    ]);
    setWatchers(ticketWatchers);
  };

  const handleWatchersSearch = (value) => {
    if (value) {
      debouncedSearch(value, ticket.scope);
    } else {
      clearUsersData({ dropdownId: 'watchers' });
    }
  };

  const handleEditDescription = () => {
    if (editDescription) {
      save();
      setEditDescription(false);
    } else {
      setEditDescription(true);
    }
  };

  const dirty = form.isFieldsTouched() || isFormDirty;

  const messageValidator = getFieldDecorator('message', {
    initialValue: ticket.message,
    rules: [
      { required: true, message: intl.formatMessage({ id: 'rules.MESSAGE' }) },
      {
        message: intl.formatMessage({ id: 'rules.MESSAGE' }),
        validator: (_, value) => {
          if (value && !sanitize(value)) {
            return Promise.reject(new Error(intl.formatMessage({ id: 'rules.MESSAGE' })));
          }
          return Promise.resolve();
        },
      },
    ],
  });

  getFieldDecorator('recepientId', {
    initialValue: ticket.recepientId,
  });
  getFieldDecorator('classificationTags', {
    initialValue: ticket.classificationTags,
  });
  getFieldDecorator('conclusionTags', {
    initialValue: ticket.conclusionTags,
  });
  getFieldDecorator('linkedEntityIds', {
    initialValue: ticket.linkedEntityIds,
  });
  getFieldDecorator('watchers', {
    initialValue: ticket.watchers?.map(w => w.id),
  });
  const disabledField = disabled || isDisabled;
  const disabledDescription = isDisabled || (ticket.message && !editDescription);

  return (
    <>
      {renderMenu()}
      {isLoading ?
        <Spinner title={intl.formatMessage({ id: 'common.LOADING_TICKET' })} />
        :
        <>
          <StyledForm layout='vertical'>
            <ErrorHandler type='notification' error={error} />
            <TicketToolbarContainer
              form={form}
              setIsFormDirty={setIsFormDirty}
              objectId={objectId}
              isNew={isNew}
              isDisabled={disabledField}
              fullPage={fullPage}
              dirty={dirty}
              setResetComment={setResetComment}
              setResetTags={setResetTags}
              resetTags={resetTags}
              parentType={context?.type}
              save={save}
              resetLinkedObjectsAndWatchers={resetToInitial}
              showSnoozeModal={() => setShowSnoozeModal(true)}
              ticketId={id}
              context={context}
              snoozeTicket={snoozeTicketRequest}
              isFormDirty={dirty}
              entityScope={ticket.scope}
              isStatusDisabled={disabled && ticket.closingReason !== 'user'}
              isLoadingToolbarInfo={isDisabled}
            />
            <StyledMainRow>
              <LeftCol span={16}>
                <Div marginBottom={24}>
                  <SubTitle>
                    <Required />
                    {`${intl.formatMessage({ id: 'ticket.DESCRIPTION' })}`}
                    {!disabled &&
                      <StyledButton onClick={handleEditDescription}>
                        {intl.formatMessage({
                          id: `tickets.${disabledDescription ? 'EDIT' : 'SAVE'}`,
                        })}
                      </StyledButton>}
                    {descriptionArrow}
                  </SubTitle>
                  {disabledDescription ?
                    descriptionContent(<TicketDescription description={ticket?.message} />) :
                    descriptionContent(
                      <FormItem>
                        {messageValidator(
                          <RichTextEditor
                            width='100%'
                            height='250px'
                            placeholder={intl.formatMessage({ id: 'tickets.TICKET_INFO' })}
                            keyBindings={{
                              '#': {
                                value: checkTags(tags),
                                cb: handleAddLinkedObject,
                              },
                            }}
                          />,
                        )}
                      </FormItem>,
                    )}
                </Div>
                {!isNew &&
                  <Div marginBottom={24}>
                    <SubTitle>
                      <FormattedMessage id='ticket.DISCUSSION' />
                      {commentArrow}
                    </SubTitle>
                    {commentContent(
                      <FormItem>
                        <Comments
                          comments={comments}
                          tags={tags}
                          addComment={addComment}
                          ticketId={ticket.id}
                          isLoading={isCommentsLoading}
                          disabled={isDisabled}
                          ticketWillSave={willSave}
                          intl={intl}
                          fullPage={fullPage}
                          setFormDirty={() => setIsFormDirty(true)}
                          resetComment={resetComment}
                          addLinkedObject={handleAddLinkedObject}
                          needsFiltering={true}
                        />
                      </FormItem>,
                    )}
                  </Div>
                }
              </LeftCol>
              <RightCol span={8}>
                {!isNew ?
                  <SmartActionSection
                    objectType={linkedSourceObject?.type}
                    id={id || objectId}
                  /> :
                  null
                }
                {!isNew && shouldRenderConclusions() ?
                  <Div marginTop={24}>
                    <Row>
                      <Col span={24}>
                        {categoryInfo?.conclusionTags &&
                          <FormItem label=''>
                            <ConclusionCard
                              selectedTags={ticket.conclusionTags || []}
                              allCategoryTags={categoryInfo?.conclusionTags}
                              form={form}
                              resetTags={resetTags}
                              setFormDirty={() => setIsFormDirty(true)}
                              categoryInfo={categoryInfo}
                              ticket={ticket}
                              isNew={isNew}
                              questions={categoryInfo?.conclusionWizard}
                              saveSuccess={success}
                            />
                          </FormItem>
                        }
                      </Col>
                    </Row>
                  </Div> :
                  null}
                <Div marginBottom={24}>
                  <SubTitle>
                    <FormattedMessage id='ticket.TICKET_WATCHERS' />
                    {watchersArrow}
                  </SubTitle>
                  {watcherContent(<LinkedList
                    disabled={disabledField}
                    icon={'eye'}
                    items={watchers?.map(w => ({
                      ...w,
                      // eslint-disable-next-line max-len
                      fullDescription: `${w.displayName}${w.email || w.mail ? ` (${w.email || w.mail})` : ''}`,
                    }))}
                    options={users.watchers?.map(w => ({
                      ...w,
                      fullDescription: `${w.displayName}${w.mail ? ` (${w.mail})` : ''}`,
                    }))}
                    addToListPlaceholder={intl.formatMessage({
                      id: 'ticket.list.ADD_WATCHER',
                    })}
                    addToListType={'string'}
                    setItems={handleUpdateWatchers}
                    onSearch={handleWatchersSearch}
                    isLoading={isLoadingUsers.watchers}
                    optionValue='fullDescription'
                    itemKey='id'
                    itemValue='fullDescription'
                  />)}
                </Div>
                <Div marginBottom={24}>
                  <SubTitle>
                    <FormattedMessage id='ticket.REFERENCED_OBJECTS' />
                    {linkedArrow}
                  </SubTitle>

                  {linkedContent(<LinkedList
                    disabled={disabledField}
                    icon={'industry'}
                    items={linkedObjects}
                    options={tags}
                    addToListPlaceholder={intl.formatMessage({
                      id: 'ticket.list.ADD_OBJECT',
                    })}
                    addToListType={'string'}
                    shouldHandleOnClick={true}
                    setItems={handleUpdateLinkedObjects}
                  />)}
                </Div>
              </RightCol>
            </StyledMainRow>
          </StyledForm>
          <Prompt
            when={dirty || isNew}
            message={
              intl.formatMessage({ id: 'prompt.UNSAVED_CHANGES' })
            }
          />
        </>
      }
      <StyledModal
        title={intl.formatMessage({ id: 'ticket.SNOOZE_TITLE' })}
        open={showSnoozeModal}
        footer={null}
        onCancel={() => setShowSnoozeModal(false)}
      >
        <SnoozeModal
          id={id}
          context={context}
          form={form}
          visible={showSnoozeModal}
          isLoading={isSnoozing}
          snoozeTicket={(params) => {
            if (dirty) {
              save(() => snoozeTicketRequest(params));
            } else {
              snoozeTicketRequest(params);
            }
          }}
          close={() => setShowSnoozeModal(false)}
        />
      </StyledModal>
      <Routes>
        <Route path={'create-order/:type/:ids/:actionId?'} element={<CreateOrders />} />
        <Route
          path={'smart-action/:actionTypeId/:actionId?'}
          element={<ActionSidepanel ticket={ticket} context={context} />}
        />
      </Routes>
      <Outlet />
    </>
  );
};

TicketForm.propTypes = {
  priorities: PropTypes.array,
  ticket: PropTypes.object,
  tags: PropTypes.array,
  comments: PropTypes.array,
  form: PropTypes.object,
  createTicket: PropTypes.func.isRequired,
  getTicket: PropTypes.func.isRequired,
  addComment: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isSaving: PropTypes.bool,
  success: PropTypes.bool,
  intl: PropTypes.object,
  isCommentsLoading: PropTypes.bool,
  fullPage: PropTypes.bool,
  error: PropTypes.object,
  resetTicketSuccess: PropTypes.func.isRequired,
  renderMenu: PropTypes.func,
  categoryInfo: PropTypes.object,
  context: PropTypes.object,
  ticketId: PropTypes.string,
  ticketFullId: PropTypes.string,
  objectId: PropTypes.string,
  linkedSourceObject: PropTypes.object,
  linkedEntities: PropTypes.array,
  saveSmartActionSuccess: PropTypes.bool,
  deleteSmartActionSuccess: PropTypes.bool,
  getCommentsByTicketId: PropTypes.func,
  isNew: PropTypes.bool,
  getTicketActionTypes: PropTypes.func,
  ticketWatchers: PropTypes.array,
  fetchRestrictedUsers: PropTypes.func,
  users: PropTypes.array,
  isLoadingUsers: PropTypes.bool,
  clearUsersData: PropTypes.func,
  addCommentSuccess: PropTypes.bool,
  isSnoozing: PropTypes.bool,
  snoozeSuccess: PropTypes.bool,
  snoozeTicketRequest: PropTypes.func,
  snoozeFailure: PropTypes.bool,
  saveTicket: PropTypes.func,
  changeNavigationContext: PropTypes.func,
  match: PropTypes.object,
};

export default Form.create()(withRouter(injectIntl(TicketForm)));
